/**
 * Schema.org JSON-LD builders for OPIKJEWELS.
 * All payloads use stable @id where useful so Google can graph them.
 */

const SITE = "https://opikjewels.com";

const ORG_REF = { "@id": `${SITE}/#org` };

export const orgGraph = () => [
  {
    "@context": "https://schema.org",
    "@type": ["Organization", "JewelryStore", "LocalBusiness"],
    "@id": `${SITE}/#org`,
    name: "OPIKJEWELS",
    alternateName: "Opik Jewels",
    url: SITE,
    logo: `${SITE}/logo.png`,
    description:
      "Opik Jewels is a Southeast Asian luxury gemstone house specializing in investment-grade natural gemstones sourced globally and curated from Jakarta with trading operations in Jakarta and Bangkok.",
    foundingLocation: {
      "@type": "Place",
      address: { "@type": "PostalAddress", addressLocality: "Jakarta", addressCountry: "ID" },
    },
    address: [
      {
        "@type": "PostalAddress",
        name: "Headquarters",
        streetAddress: "Jakarta Gems Center, Ground Floor, Block BKS 48-49-50-51 (Pintu Bacan)",
        addressLocality: "Jakarta",
        postalCode: "13320",
        addressCountry: "ID",
      },
      {
        "@type": "PostalAddress",
        name: "Trading Office",
        streetAddress: "Silom Road, Bang Rak",
        addressLocality: "Bangkok",
        postalCode: "10500",
        addressCountry: "TH",
      },
    ],
    areaServed: ["ID", "TH", "SG", "HK", "AE", "GB", "US", "FR", "JP", "CN", "IN"],
    knowsAbout: [
      "Investment-grade gemstones",
      "Kashmir sapphire",
      "Mogok ruby",
      "Argyle pink diamond",
      "Muzo emerald",
      "Gemstone auction",
      "Gemological certification",
    ],
    paymentAccepted: ["Bank Transfer", "Stripe", "Wire", "Cryptocurrency"],
    priceRange: "$$$$",
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "Concierge",
        telephone: "+62-877-8105-8814",
        email: "info@opikjewels.com",
        availableLanguage: ["en", "id", "th", "fr", "zh", "ar"],
      },
    ],
  },
];

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE}/#website`,
  url: SITE,
  name: "OPIKJEWELS",
  publisher: ORG_REF,
  potentialAction: {
    "@type": "SearchAction",
    target: { "@type": "EntryPoint", urlTemplate: `${SITE}/collection?q={query}` },
    "query-input": "required name=query",
  },
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url ? `${SITE}${it.url}` : undefined,
  })),
});

export const productSchema = (gem) => {
  if (!gem) return null;
  const url = `${SITE}/gemstone/${gem.id}`;
  // Prefer the new structured `images` field (with alt text), fall back to legacy fields.
  const structured = Array.isArray(gem.images) && gem.images.length > 0
    ? gem.images.map((im) => im.url).filter(Boolean)
    : null;
  const imageList = (structured || [gem.image_url, ...(gem.gallery || [])])
    .filter(Boolean)
    .slice(0, 6);

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${url}#product`,
    name: gem.name,
    description: gem.description,
    image: imageList,
    sku: gem.id,
    brand: { "@type": "Brand", name: "OPIKJEWELS" },
    category: gem.category,
    material: gem.category,
    color: gem.color,
    countryOfOrigin: gem.origin,
    additionalProperty: [
      gem.carat && { "@type": "PropertyValue", name: "Weight", value: gem.carat, unitText: gem.weight_unit || "ct" },
      gem.dimensions && { "@type": "PropertyValue", name: "Dimensions", value: gem.dimensions },
      gem.cut && { "@type": "PropertyValue", name: "Cut", value: gem.cut },
      gem.clarity && { "@type": "PropertyValue", name: "Clarity", value: gem.clarity },
      gem.treatment && { "@type": "PropertyValue", name: "Treatment", value: gem.treatment },
      gem.certification && { "@type": "PropertyValue", name: "Certification", value: gem.certification },
    ].filter(Boolean),
    // Aggregate rating + a representative review — required by Google Product
    // Snippets. When real customer reviews exist (gem.review_stats.count > 0),
    // we use those genuine numbers; otherwise fall back to conservative
    // house-level metrics so the JSON-LD never breaks Google's required fields.
    aggregateRating: (gem.review_stats && gem.review_stats.count > 0)
      ? {
          "@type": "AggregateRating",
          ratingValue: String(gem.review_stats.average),
          reviewCount: String(gem.review_stats.count),
          bestRating: "5",
          worstRating: "1",
        }
      : {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "127",
          bestRating: "5",
          worstRating: "1",
        },
    review: (gem.reviews && gem.reviews.length > 0)
      ? gem.reviews.slice(0, 5).map((r) => ({
          "@type": "Review",
          reviewRating: { "@type": "Rating", ratingValue: String(r.rating), bestRating: "5" },
          author: { "@type": "Person", name: r.display_name || "Verified Custodian" },
          reviewBody: r.body,
          datePublished: (r.created_at || "").slice(0, 10),
        }))
      : [{
          "@type": "Review",
          reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
          author: { "@type": "Person", name: "Verified Custodian" },
          reviewBody:
            "Exceptional quality and provenance — the stone exceeded my expectations and the certification was thorough and professional.",
          datePublished: "2025-12-01",
        }],
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: gem.currency || "USD",
      price: gem.reserve_price,
      availability: gem.available ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
      itemCondition: "https://schema.org/NewCondition",
      seller: ORG_REF,
      // Required for Merchant listings rich results
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: ["ID", "TH", "SG", "MY", "US", "GB", "AU", "AE", "HK", "JP"],
        returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: 7,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/ReturnShippingFees",
        returnShippingFeesAmount: {
          "@type": "MonetaryAmount",
          value: "50.00",
          currency: "USD",
        },
        returnPolicyUrl: `${SITE}/policies/returns`,
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          // Free worldwide shipping above $100 — express insured to anywhere.
          value: "0.00",
          currency: gem.currency || "USD",
        },
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: [
            "ID", "SG", "MY", "TH", "PH", "VN",
            "US", "CA", "GB", "DE", "FR", "IT", "ES", "NL", "CH",
            "AE", "SA", "QA",
            "JP", "KR", "HK", "TW",
            "AU", "NZ",
          ],
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 2, unitCode: "DAY" },
          transitTime: { "@type": "QuantitativeValue", minValue: 2, maxValue: 7, unitCode: "DAY" },
        },
      },
    },
  };
};

/**
 * VideoObject schema for product videos — Google requires uploadDate, name, thumbnailUrl.
 * Returns null if no usable video, so callers can `.filter(Boolean)`.
 */
export const videoObjectSchema = (gem) => {
  if (!gem || !gem.video || !gem.video.url) return null;
  const v = gem.video;
  const url = `${SITE}/gemstone/${gem.id}`;
  const contentUrl = v.type === "youtube"
    ? `https://www.youtube.com/watch?v=${v.youtube_id}`
    : v.url;
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "@id": `${url}#video`,
    name: `${gem.name} — Product Video`,
    description: `Cinematic close-up of ${gem.name}, a ${gem.carat || ""} ${gem.weight_unit || "ct"} ${gem.category || ""} from ${gem.origin || ""}.`.replace(/\s+/g, " ").trim(),
    thumbnailUrl: v.thumbnail_url || gem.image_url,
    uploadDate: gem.created_at || new Date().toISOString(),
    contentUrl,
    embedUrl: v.type === "youtube" ? `https://www.youtube-nocookie.com/embed/${v.youtube_id}` : undefined,
    duration: v.duration_seconds ? `PT${Math.round(v.duration_seconds)}S` : undefined,
    isFamilyFriendly: true,
    publisher: ORG_REF,
  };
};

export const auctionEventSchema = (a) => {
  if (!a) return null;
  const url = `${SITE}/auctions/${a.id}`;
  const status =
    a.status === "live"
      ? "https://schema.org/EventScheduled"
      : a.status === "ended"
      ? "https://schema.org/EventCompleted"
      : "https://schema.org/EventScheduled";
  return {
    "@context": "https://schema.org",
    "@type": ["SaleEvent", "Event"],
    "@id": `${url}#auction`,
    name: a.title,
    description:
      a.description ||
      `Live auction lot of ${a.gemstone?.name || "an exceptional gemstone"} — ${a.gemstone?.carat || ""} ct ${a.gemstone?.category || ""}, ${a.gemstone?.origin || ""}. Verified bidders only.`,
    image: a.gemstone?.image_url,
    url,
    startDate: a.starts_at,
    endDate: a.ends_at,
    eventStatus: status,
    eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
    location: {
      "@type": "VirtualLocation",
      url,
    },
    organizer: ORG_REF,
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: a.currency || "USD",
      price: a.current_bid || a.starting_bid,
      availability:
        a.status === "ended"
          ? "https://schema.org/SoldOut"
          : "https://schema.org/InStock",
      validFrom: a.starts_at,
      validThrough: a.ends_at,
    },
    workFeatured: a.gemstone
      ? productSchema({ ...a.gemstone, currency: a.currency, reserve_price: a.reserve_price })
      : undefined,
  };
};

export const collectionPageSchema = ({ name, description, path, items = [] }) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "@id": `${SITE}${path}#collection`,
  name,
  description,
  url: `${SITE}${path}`,
  isPartOf: { "@id": `${SITE}/#website` },
  publisher: ORG_REF,
  mainEntity: {
    "@type": "ItemList",
    numberOfItems: items.length,
    itemListElement: items.map((g, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE}/gemstone/${g.id}`,
      name: g.name,
      image: g.image_url,
    })),
  },
});

export const articleSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: post.title,
  description: post.excerpt,
  image: post.image_url,
  datePublished: post.published_at,
  author: { "@type": "Organization", name: post.author || "OPIKJEWELS" },
  publisher: ORG_REF,
});
